import { useState, useCallback } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '@/utils/products/constants';
import { stringToValues, validateAttribute } from '@/utils/products/attributeHelpers';
import { generateVariantsFromAttributes, mergeVariants } from '@/utils/products/variantHelpers';

export const useProductAttributes = (product, setProduct, showNotification) => {
    // Add temporary states for attributes and variants
    const [tempAttributes, setTempAttributes] = useState([]);
    const [tempVariants, setTempVariants] = useState([]);
    const [unsavedAttributeChanges, setUnsavedAttributeChanges] = useState(false);
    
    // New attribute state
    const [newAttribute, setNewAttribute] = useState({ name: "", values: "" });
    
    // Edit attribute state
    const [editingAttributeIndex, setEditingAttributeIndex] = useState(-1);
    
    // Handle adding a new attribute
    const handleAddAttribute = async () => {
        if (!newAttribute.name.trim()) {
            showNotification("Attribute name is required", "error");
            return;
        }
        
        const values = stringToValues(newAttribute.values);
        if (values.length === 0) {
            showNotification("Attribute values are required", "error");
            return;
        }
        
        try {
            if (editingAttributeIndex >= 0) {
                // Update existing attribute in temp state
                const updatedAttributes = [...tempAttributes];
                updatedAttributes[editingAttributeIndex] = {
                    ...updatedAttributes[editingAttributeIndex],
                    name: newAttribute.name.trim(),
                    values: values
                };
                
                setTempAttributes(updatedAttributes);
                setEditingAttributeIndex(-1);
                showNotification("Attribute updated", "success");
                
                // Generate new variants based on updated attributes
                const newVariants = generateVariantsFromAttributes(updatedAttributes);
                setTempVariants(mergeVariants(tempVariants, newVariants));
            } else {
                // Add new attribute to temp state
                const newAttr = {
                    // Generate a temporary ID (will be replaced with real ID after saving)
                    id: `temp_${Date.now()}`,
                    product_id: product.productId,
                    name: newAttribute.name.trim(),
                    values: values,
                    created_at: Date.now()
                };
                
                const updatedAttributes = [...tempAttributes, newAttr];
                setTempAttributes(updatedAttributes);
                showNotification("Attribute added", "success");
                
                // Generate new variants based on updated attributes
                const newVariants = generateVariantsFromAttributes(updatedAttributes);
                setTempVariants(mergeVariants(tempVariants, newVariants));
            }
            
            // Mark that we have unsaved changes
            setUnsavedAttributeChanges(true);
            
            // Reset form
            setNewAttribute({ name: "", values: "" });
        } catch (error) {
            console.error("Error adding/updating attribute:", error);
            showNotification(error.message || "Failed to manage attribute", "error");
        }
    };
    
    // Handle edit attribute
    const handleEditAttribute = (index) => {
        const attribute = tempAttributes[index];
        setNewAttribute({
            name: attribute.name,
            values: Array.isArray(attribute.values) 
                ? attribute.values.map(v => typeof v === 'object' ? v.value : v).join(', ')
                : ''
        });
        setEditingAttributeIndex(index);
    };
    
    // Handle remove attribute
    const handleRemoveAttribute = async (index) => {
        if (window.confirm("Are you sure you want to remove this attribute? This will also update product variants.")) {
            try {
                const updatedAttributes = [...tempAttributes];
                updatedAttributes.splice(index, 1);
                
                setTempAttributes(updatedAttributes);
                
                // If we still have attributes, regenerate variants
                if (updatedAttributes.length > 0) {
                    const newVariants = generateVariantsFromAttributes(updatedAttributes);
                    setTempVariants(mergeVariants(tempVariants, newVariants));
                } else {
                    // If no attributes left, clear variants
                    setTempVariants([]);
                }
                
                showNotification("Attribute removed", "success");
                
                // Mark that we have unsaved changes
                setUnsavedAttributeChanges(true);
                
                // If currently editing this attribute, reset the form
                if (editingAttributeIndex === index) {
                    setNewAttribute({ name: "", values: "" });
                    setEditingAttributeIndex(-1);
                }
            } catch (error) {
                console.error("Error removing attribute:", error);
                showNotification(error.message || "Failed to remove attribute", "error");
            }
        }
    };
    
    // Cancel attribute editing
    const handleCancelEdit = () => {
        setNewAttribute({ name: "", values: "" });
        setEditingAttributeIndex(-1);
    };
    
    // Handle attribute input change
    const handleAttributeInputChange = (e) => {
        const { name, value } = e.target;
        setNewAttribute(prev => ({
            ...prev,
            [name]: value
        }));
    };
    
    // Handle saving attributes to database
    const handleSaveAttributes = async () => {
        try {
            // First delete any existing attributes that were removed
            for (const attr of product.attributes) {
                if (!tempAttributes.find(a => a.id === attr.id)) {
                    // This attribute was removed in the temp state, delete it
                    await axios.post(`${API_BASE_URL}/product-variant/attribute/${attr.id}/delete`);
                }
            }
            
            // Then add all temp attributes (both new and updated)
            const updatedAttributes = [];
            
            for (const attr of tempAttributes) {
                // Determine if this is a new attribute or an edited one
                const values = Array.isArray(attr.values) 
                    ? attr.values.map(v => typeof v === 'object' ? v.value : v)
                    : [];
                
                const response = await axios.post(`${API_BASE_URL}/product-variant/attribute/create`, {
                    name: attr.name,
                    values: values,
                    productId: product.productId
                });
                
                if (response.data.isSuccess) {
                    updatedAttributes.push(response.data.data);
                }
            }
            
            // Generate new variants based on updated attributes
            const newVariants = generateVariantsFromAttributes(updatedAttributes);
            const mergedVariants = mergeVariants(tempVariants, newVariants);
            
            // Save the generated variants to the API
            for (const variant of mergedVariants) {
                // Skip variants that already exist on the server (have non-temporary IDs)
                if (variant.id && !variant.id.startsWith('variant_')) {
                    // Update existing variant if needed
                    if (variant.price || variant.is_active || variant.is_locked) {
                        await axios.post(
                            `${API_BASE_URL}/product-variant/variant/update`, 
                            {
                                id: variant.id,
                                price: variant.price || 0,
                                is_active: variant.is_active || false,
                                is_locked: variant.is_locked || false
                            }
                        );
                    }
                    continue;
                }
                
                // Add new variant
                try {
                    // Map variant attributes to their value IDs from the updated attributes
                    const attributeValueIds = variant.attributes.map(varAttr => {
                        // Find the corresponding attribute in updatedAttributes
                        const attribute = updatedAttributes.find(attr => attr.name === varAttr.name);
                        if (!attribute) return null;
                        
                        // Find the value object in the attribute's values array that matches our variant value
                        const valueObj = attribute.values.find(v => v.value === varAttr.value);
                        return valueObj ? valueObj.id : null;
                    }).filter(id => id !== null); // Remove any null values
                    
                    const variantResponse = await axios.post(
                        `${API_BASE_URL}/product-variant/variant/create`,
                        {
                            price: variant.price || 0,
                            is_active: variant.is_active || false,
                            is_locked: variant.is_locked || false,
                            attributeValues: attributeValueIds,
                            productId: product.productId
                        }
                    );
                    
                    if (variantResponse.data.isSuccess) {
                        // Update the variant ID with the one from the server
                        variant.id = variantResponse.data.data.id;
                    }
                } catch (variantError) {
                    console.error("Error adding variant:", variantError);
                    // Continue with other variants even if one fails
                }
            }
            
            // Update product state with the saved attributes and variants
            setProduct(prev => ({
                ...prev,
                attributes: updatedAttributes,
                variants: mergedVariants
            }));
            
            showNotification("Attributes saved", "success");
            setUnsavedAttributeChanges(false);
            
            return true; // Indicate success
        } catch (error) {
            console.error("Error saving attributes:", error);
            showNotification(error.message || "Failed to save attributes", "error");
            return false; // Indicate failure
        }
    };
    
    return {
        tempAttributes,
        setTempAttributes,
        tempVariants,
        setTempVariants,
        unsavedAttributeChanges,
        setUnsavedAttributeChanges,
        newAttribute,
        setNewAttribute,
        editingAttributeIndex,
        setEditingAttributeIndex,
        handleAddAttribute,
        handleEditAttribute,
        handleRemoveAttribute,
        handleCancelEdit,
        handleAttributeInputChange,
        handleSaveAttributes
    };
};